import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    Users,
    UserPlus,
    Clock,
    CreditCard,
    CheckCircle2,
    Rocket,
    XCircle,
    IndianRupee,
    LifeBuoy,
    Sparkles,
    RefreshCw,
    Globe,
    ShieldAlert,
    TrendingUp,
    TrendingDown,
    ListTodo,
    FileText,
    ArrowRight,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { inr } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function Scorecard({ label, value, delta, icon: Icon, testid, link, tint = "blue" }) {
    const tints = {
        blue: "text-[#3A80F7] bg-[#EEF4FE]",
        green: "text-[#16A34A] bg-[#E7F7EE]",
        amber: "text-[#B45309] bg-[#FEF3C7]",
        red: "text-[#DC2626] bg-[#FEE2E2]",
        purple: "text-[#8B5CF6] bg-[#F5F3FF]",
    };
    const t = tints[tint] || tints.blue;
    const Body = (
        <div className="ddi-stat-card ddi-lift" data-testid={testid}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">{label}</div>
                {Icon && <div className={"h-8 w-8 rounded-lg flex items-center justify-center " + t}><Icon size={14} /></div>}
            </div>
            <div className="ddi-stat-value">{value}</div>
            {delta !== undefined && delta !== null && (
                <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold">
                    {delta > 0 ? <TrendingUp size={12} className="text-[#16A34A]" /> : delta < 0 ? <TrendingDown size={12} className="text-[#DC2626]" /> : null}
                    <span className={delta > 0 ? "text-[#16A34A]" : delta < 0 ? "text-[#DC2626]" : "text-[#94A3B8]"}>
                        {delta > 0 ? "+" : ""}{delta}% vs prev period
                    </span>
                </div>
            )}
        </div>
    );
    return link ? <Link to={link}>{Body}</Link> : Body;
}

function LineChart({ series, valueKey, color = "#3A80F7", height = 100 }) {
    const values = series.map((s) => s[valueKey]);
    const max = Math.max(1, ...values);
    const min = Math.min(0, ...values);
    const w = 100, h = height;
    const step = series.length > 1 ? w / (series.length - 1) : 0;
    const pts = series.map((s, i) => {
        const y = h - ((s[valueKey] - min) / (max - min || 1)) * h;
        return `${(i * step).toFixed(2)},${y.toFixed(2)}`;
    }).join(" ");
    const area = `0,${h} ${pts} ${w},${h}`;
    return (
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: `${h}px` }}>
            <defs>
                <linearGradient id={`grad-${color.slice(1)}`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <polyline points={area} fill={`url(#grad-${color.slice(1)})`} stroke="none" />
            <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

function Donut({ items, colors }) {
    const total = items.reduce((a, b) => a + b.count, 0) || 1;
    const cx = 60, cy = 60, r = 44, sw = 16;
    let acc = 0;
    const arcs = items.map((it, i) => {
        const frac = it.count / total;
        const start = acc * 2 * Math.PI - Math.PI / 2;
        acc += frac;
        const end = acc * 2 * Math.PI - Math.PI / 2;
        const large = frac > 0.5 ? 1 : 0;
        const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
        return (
            <path
                key={it.code}
                d={`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`}
                stroke={colors[i % colors.length]}
                strokeWidth={sw}
                fill="none"
            />
        );
    });
    return (
        <div className="flex items-center gap-4">
            <svg width={120} height={120} viewBox="0 0 120 120">{arcs}</svg>
            <ul className="text-[12px] space-y-1">
                {items.map((it, i) => (
                    <li key={it.code} className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: colors[i % colors.length] }} />
                        <span className="text-[#334155] capitalize">{(it.code || "unassigned").replace("_", " ")}</span>
                        <span className="text-[#94A3B8] ml-1">{it.count}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}


export default function AdminDashboard() {
    const [summary, setSummary] = useState(null);
    const [charts, setCharts] = useState(null);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [m, c] = await Promise.all([
                api.get("/admin/metrics/summary"),
                api.get("/admin/metrics/charts?days=30"),
            ]);
            setSummary(m.data);
            setCharts(c.data);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    if (!summary) {
        return (
            <AdminShell>
                <div className="text-[#64748B]">Loading operations dashboard…</div>
            </AdminShell>
        );
    }

    const t = summary.totals;
    const r7 = summary.registrations;
    const rev = summary.revenue_inr;

    return (
        <AdminShell>
            <div className="mb-6 flex items-center justify-between" data-testid="admin-dashboard-header">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">{new Date().toDateString()}</div>
                    <h1 className="text-3xl font-bold text-[#0F172A] mt-1 tracking-tight">Operations Dashboard</h1>
                    <p className="text-[#64748B] mt-1 text-[14px]">Everything you need to run the platform, in one view.</p>
                </div>
                <button onClick={load} disabled={loading} className="ddi-btn-outline" data-testid="admin-refresh">
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {/* Top scorecards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <Scorecard label="Total Doctors" value={t.doctors} icon={Users} testid="metric-doctors" link="/admin/doctors" />
                <Scorecard label="New · 7 days" value={r7.last_7d} delta={r7.pct_change_7d} icon={UserPlus} testid="metric-new-7d" tint="green" />
                <Scorecard label="New · 30 days" value={r7.last_30d} delta={r7.pct_change_30d} icon={UserPlus} testid="metric-new-30d" tint="green" />
                <Scorecard label="Published Websites" value={t.published} icon={Rocket} testid="metric-published" tint="green" link="/admin/publishing" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <Scorecard label="Revenue · this month" value={inr(rev.this_month)} icon={IndianRupee} testid="metric-rev-month" tint="green" />
                <Scorecard label="Revenue · YTD" value={inr(rev.this_year)} icon={IndianRupee} testid="metric-rev-year" tint="green" />
                <Scorecard label="Failed Payments" value={t.failed_payments} icon={XCircle} testid="metric-failed-pay" tint="red" link="/admin/payments?status=failed" />
                <Scorecard label="AMC Overdue" value={t.amc_overdue} icon={Clock} testid="metric-amc-overdue" tint="amber" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Scorecard label="Pending Reviews" value={t.pending_reviews} icon={ListTodo} testid="metric-reviews" tint="amber" link="/admin/queue" />
                <Scorecard label="Publishing Queue" value={t.publishing_queue} icon={Rocket} testid="metric-publishing" tint="blue" link="/admin/publishing" />
                <Scorecard label="AI Jobs · queued" value={summary.ai_jobs.queued} icon={Sparkles} testid="metric-ai-queued" />
                <Scorecard label="AI Jobs · failed" value={summary.ai_jobs.failed} icon={Sparkles} testid="metric-ai-failed" tint="red" link="/admin/ai-jobs?status=failed" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Scorecard label="Open Tickets" value={t.open_tickets} icon={LifeBuoy} testid="metric-tickets" tint="amber" link="/admin/support" />
                <Scorecard label="Attention · alerts" value={t.profiles_needing_attention} icon={ShieldAlert} testid="metric-attention" tint="amber" link="/admin/operations" />
                <Scorecard label="Domain Requests" value={t.open_domain_requests} icon={Globe} testid="metric-domains" tint="blue" />
                <Scorecard label="AMC Due · 30d" value={t.amc_due} icon={Clock} testid="metric-amc-due" tint="amber" />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <Card className="border-[#E5E9F2] ddi-shadow-sm lg:col-span-2">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-[13px] text-[#334155] flex items-center gap-2">
                            <TrendingUp size={14} className="text-[#3A80F7]" /> Revenue · last 30 days
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-2 flex items-baseline gap-3">
                            <div className="text-[24px] font-bold text-[#0F172A]" data-testid="chart-rev-total">{inr(rev.last_30d)}</div>
                            <span className="text-[12px] text-[#94A3B8]">total</span>
                        </div>
                        {charts && <LineChart series={charts.revenue_series} valueKey="amount_inr" color="#3A80F7" />}
                    </CardContent>
                </Card>

                <Card className="border-[#E5E9F2] ddi-shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-[13px] text-[#334155]">Doctors by package</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {charts && (
                            <Donut
                                items={charts.doctors_by_package}
                                colors={["#3A80F7", "#0F766E", "#8B5CF6", "#F59E0B", "#DC2626", "#94A3B8"]}
                            />
                        )}
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <Card className="border-[#E5E9F2] ddi-shadow-sm lg:col-span-2">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-[13px] text-[#334155]">Registrations · last 30 days</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-2 text-[24px] font-bold text-[#0F172A]" data-testid="chart-reg-total">{r7.last_30d}</div>
                        {charts && <LineChart series={charts.registrations_series} valueKey="count" color="#16A34A" />}
                    </CardContent>
                </Card>

                <Card className="border-[#E5E9F2] ddi-shadow-sm">
                    <CardHeader className="pb-3"><CardTitle className="text-[13px] text-[#334155]">Profile-status funnel</CardTitle></CardHeader>
                    <CardContent>
                        <ul className="space-y-1.5 text-[12px]">
                            {(charts?.profile_funnel || []).map((row) => {
                                const max = Math.max(...(charts.profile_funnel.map((r) => r.count) || [1]), 1);
                                const pct = Math.round((row.count / max) * 100);
                                return (
                                    <li key={row.stage} className="flex items-center gap-2" data-testid={`funnel-${row.stage}`}>
                                        <span className="w-32 text-[#334155] capitalize">{row.stage.replaceAll("_", " ")}</span>
                                        <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                                            <div className="h-full bg-[#3A80F7]" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="text-[#0F172A] font-semibold w-8 text-right">{row.count}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </CardContent>
                </Card>
            </div>

            {/* Quick access */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                    { to: "/admin/queue", label: "Approvals", icon: ListTodo },
                    { to: "/admin/doctors", label: "Doctors", icon: Users },
                    { to: "/admin/payments", label: "Payments", icon: CreditCard },
                    { to: "/admin/ai-jobs", label: "AI Jobs", icon: Sparkles },
                    { to: "/admin/publishing", label: "Publishing", icon: Rocket },
                    { to: "/admin/leads", label: "Leads", icon: Users },
                    { to: "/admin/activity", label: "Activity", icon: FileText },
                    { to: "/admin/support", label: "Support", icon: LifeBuoy },
                ].map((a) => {
                    const Icon = a.icon;
                    return (
                        <Link key={a.to} to={a.to} className="ddi-card p-3 flex items-center gap-2 hover:border-[#3A80F7] transition-colors" data-testid={`quick-admin-${a.label.toLowerCase()}`}>
                            <div className="h-8 w-8 rounded-lg bg-[#EEF4FE] text-[#3A80F7] flex items-center justify-center">
                                <Icon size={14} />
                            </div>
                            <span className="text-[13px] font-semibold text-[#0F172A]">{a.label}</span>
                            <ArrowRight size={12} className="ml-auto text-[#94A3B8]" />
                        </Link>
                    );
                })}
            </div>
        </AdminShell>
    );
}
